export async function fetchMetafieldDetailsForProducts(productIds) {
  // Validate and construct the endpoint URL using URL constructor.
  const shopDomain = window.Shopify?.shop;

  if (!shopDomain) {
    throw new Error('Shopify shop domain is not available.');
  }

  // Use URL.canParse() to validate the URL before construction.
  const relativePath = '/api/2026-01/graphql.json';
  const baseUrlString = `https://${shopDomain}`;

  if (!URL.canParse(relativePath, baseUrlString)) {
    throw new Error('Invalid URL construction parameters.');
  }

  const baseUrl = new URL(relativePath, baseUrlString);
  const endpoint = baseUrl.toString();

  // Validate access token availability.
  const accessToken = window.storefrontAccessToken?.tagalys;
  if (!accessToken) {
    throw new Error('Storefront access token is not available.');
  }

  const gidWorkaround = 'gid:' + '/' + `/shopify/Product/`;
  const query = `
query getProductsWithMetaobjects($ids: [ID!]!) {
  nodes(ids: $ids) {
    ... on Product {
      id
      handle
      title
      metafields(
        identifiers: [
        {namespace: "app--${window.tagalysConfig.environmentAppid}--pdp", key: "colour"}, 
        {namespace: "app--${window.tagalysConfig.environmentAppid}--pdp", key: "color_variants"}, 
        {namespace: "app--${window.tagalysConfig.environmentAppid}--pdp", key: "stickers"}, 
        {namespace: "app--${window.tagalysConfig.environmentAppid}--pdp", key: "material"}, 
        {namespace: "app--${window.tagalysConfig.environmentAppid}--personalisation", key: "reference"},
        {namespace: "app--${window.tagalysConfig.environmentAppid}--personalisation", key: "hasOnlyUpsells"},
        {namespace: "plp", key: "delivery_badge_label"},
        {namespace: "plp", key: "delivery_badge_info"},
        {namespace: "plp", key: "delivery_badge_background_colour"},
        {namespace: "plp", key: "delivery_badge_text_colour"}
        ]
      ) {
        namespace
        key
        id
        value
        type
        reference {
          ... on Metaobject {
            id
            badge_label: field(key: "badge_label") {
              value
            }
            label: field(key: "label") {
              value
            }
            colourImage: field(key: "image") {
              reference {
                ... on MediaImage {
                  image {
                    url
                  }
                }
              }
            }
            title: field(key: "title") {
              value
            }
            content: field(key: "content") {
              value
            }
          }
          ... on Page {
            id
            title
            body
          }
        }
        references(first: 50) {
          nodes {
            ... on Product {
              id
              title
              handle
              material: metafield(namespace: "app--${window.tagalysConfig.environmentAppid}--pdp", key: "material") {
                reference {
                  ... on Metaobject {
                    label: field(key: "label") {
                      value
                    }
                  }
                }
              }
              colour: metafield(namespace: "app--${window.tagalysConfig.environmentAppid}--pdp", key: "colour") {
                reference {
                  ... on Metaobject {
                    id
                    label: field(key: "label") {
                      value
                    }
                    colourImage: field(key: "image") {
                      reference {
                        ... on MediaImage {
                          id
                          alt
                          image {
                            url
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            ... on Metaobject {
              id
              label: field(key: "label") {
                value
              }
              colour: field(key: "colour") {
                value
              }
              textColour: field(key: "textColour") {
                value
              }
              priority: field(key: "priority") {
                value
              }
            }
          }
        }
      }
    }
  }
}
  `;

  // Convert product IDs to Shopify GID format if they aren't already
  const formattedIds = productIds.map((id) => {
    return gidWorkaround + id;
  });

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': accessToken
      },
      body: JSON.stringify({
        query,
        variables: {
          ids: formattedIds
        }
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    // Process and return the results
    const productDetailsToMetafields = data.data.nodes
      .filter((product) => product !== null)
      .reduce((acc, product) => {
        let materialData = false;
        let colourSwatchData = false;
        let colourSwatchImageData = false;
        // The 'badgeData' variable is replaced with 'stickersData', initialized as an empty array.
        let stickersData = [];
        let personaliseData = false;
        let deliveryBadgeLabelData = '';
        let deliveryBadgeInfoData = null;
        let deliveryBadgeBackgroundColourData = '';
        let deliveryBadgeTextColourData = '';
        const productId = product.id.replace(gidWorkaround, '');

        const colorVariants = product.metafields.filter(
          (reference) => reference?.namespace.endsWith('pdp') && reference?.key === 'color_variants'
        );

        const material = product.metafields.find(
          (item) => item?.namespace.endsWith('pdp') && item?.key === 'material' && item?.reference
        );
        if (material) {
          materialData = material.reference.label.value;
        }

        // Find the personalisation reference metafield
        const personalise = product.metafields.find(
          (reference) => reference?.namespace.endsWith('personalisation') && reference?.key === 'reference'
        );

        // Find the 'hasOnlyUpsells' metafield
        const hasOnlyUpsellsMetafield = product.metafields.find(
          (reference) => reference?.namespace.endsWith('personalisation') && reference?.key === 'hasOnlyUpsells'
        );

        // Check if hasOnlyUpsells is true
        const hasOnlyUpsells = hasOnlyUpsellsMetafield?.value === true || hasOnlyUpsellsMetafield?.value === 'true';

        // Only set personaliseData to true if the reference exists and hasOnlyUpsells is false
        if (personalise && !hasOnlyUpsells) {
          personaliseData = typeof personalise.reference?.id === 'string';
        }

        const colourSwatch = product.metafields.find(
          (reference) => reference?.namespace.endsWith('pdp') && reference?.key === 'colour'
        );
        if (colourSwatch) {
          colourSwatchData = colourSwatch.reference.label.value;
          colourSwatchImageData = colourSwatch.reference.colourImage?.reference?.image?.url;
        }

        // This block finds the 'stickers' metafield.
        // If it exists, it maps over the referenced metaobjects to create an array of sticker objects.
        const stickers = product.metafields.find(
          (reference) => reference?.namespace.endsWith('pdp') && reference?.key === 'stickers'
        );
        if (stickers && stickers.references) {
          stickersData = stickers.references.nodes
            .map((sticker) => {
              if (!sticker) return null;
              return {
                label: sticker.label?.value,
                colour: sticker.colour?.value,
                textColour: sticker.textColour?.value,
                priority: sticker.priority?.value ? parseInt(sticker.priority.value, 10) : null
              };
            })
            .filter(Boolean);
        }

        // This block finds the 'delivery badge' metafields.
        // - Label | Info | Background Colour | Text Colour
        const deliveryBadgeLabel = product.metafields.find(
          (reference) => reference?.namespace === 'plp' && reference?.key === 'delivery_badge_label'
        );

        if (deliveryBadgeLabel) {
          deliveryBadgeLabelData = deliveryBadgeLabel.value || '';
        }

        const deliveryBadgeInfo = product.metafields.find(
          (reference) =>
            reference?.namespace === 'plp' && reference?.key === 'delivery_badge_info' && reference?.reference
        );
        if (deliveryBadgeInfo?.reference?.title) {
          deliveryBadgeInfoData = {
            title: deliveryBadgeInfo.reference.title,
            content: deliveryBadgeInfo.reference.body || ''
          };
        }

        const deliveryBadgeBackgroundColour = product.metafields.find(
          (reference) => reference?.namespace === 'plp' && reference?.key === 'delivery_badge_background_colour'
        );
        if (deliveryBadgeBackgroundColour) {
          deliveryBadgeBackgroundColourData = deliveryBadgeBackgroundColour.value || '';
        }

        const deliveryBadgeTextColour = product.metafields.find(
          (reference) => reference?.namespace === 'plp' && reference?.key === 'delivery_badge_text_colour'
        );
        if (deliveryBadgeTextColour) {
          deliveryBadgeTextColourData = deliveryBadgeTextColour.value || '';
        }

        const colorVariantData = colorVariants
          .flatMap((colorVariant) => {
            const { references } = colorVariant;
            return references.nodes.map(({ colour, handle }) => {
              // Bail out if the metafield is not found.
              if (!colour) return null;
              const {
                reference: { label, colourImage }
              } = colour;

              if (!label) return null;
              return {
                label: label.value,
                image: colourImage.reference.image.url,
                alt: colourImage.reference.alt,
                productHandle: handle
              };
            });
          })
          .filter(Boolean);

        acc[`${productId}`] = {
          colorVariants: colorVariantData,
          material: materialData,
          colourSwatch: colourSwatchData,
          colourSwatchImage: colourSwatchImageData,
          stickers: stickersData,
          personalise: personaliseData,
          deliveryBadgeLabel: deliveryBadgeLabelData,
          deliveryBadgeInfo: deliveryBadgeInfoData,
          deliveryBadgeBackgroundColour: deliveryBadgeBackgroundColourData,
          deliveryBadgeTextColour: deliveryBadgeTextColourData
        };
        return acc;
      }, {});
    return productDetailsToMetafields;
  } catch (error) {
    console.error('Error fetching product group metaobjects:', error);
    throw error;
  }
}
